import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import VendorCard from './VendorCard';
import { VendorPublicProfile } from '../types';

interface VendorCarouselProps {
  vendors: VendorPublicProfile[];
  title?: string;
  autoRotate?: boolean;
  autoRotateInterval?: number;
  itemsPerPage?: number;
}

export default function VendorCarousel({
  vendors,
  title = 'Featured Vendors',
  autoRotate = false,
  autoRotateInterval = 5000,
  itemsPerPage = 3,
}: VendorCarouselProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const totalPages = Math.ceil(vendors.length / itemsPerPage);
  const canNavigate = totalPages > 1;

  const goToNext = useCallback(() => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const goToPrevious = useCallback(() => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  }, [totalPages]);

  // Auto-rotate carousel
  useEffect(() => {
    if (!autoRotate || !canNavigate || isPaused) {
      return;
    }

    const interval = setInterval(goToNext, autoRotateInterval);
    return () => clearInterval(interval);
  }, [autoRotate, autoRotateInterval, canNavigate, isPaused, goToNext]);

  if (vendors.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <p className="text-gray-600 mb-2">No vendors to display.</p>
        <p className="text-sm text-gray-500">Check back soon!</p>
      </div>
    );
  }

  const startIndex = currentPage * itemsPerPage;
  const visibleVendors = vendors.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Header */}
      {title && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary-dark">{title}</h2>
          <div className="text-sm text-gray-600">
            {vendors.length} vendor{vendors.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Carousel Container */}
      <div className="relative">
        {/* Navigation Buttons */}
        {canNavigate && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous vendors"
              disabled={!canNavigate}
            >
              <ChevronLeft className="h-6 w-6 text-gray-700" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next vendors"
              disabled={!canNavigate}
            >
              <ChevronRight className="h-6 w-6 text-gray-700" />
            </button>
          </>
        )}

        {/* Vendor Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleVendors.map((vendor) => (
            <VendorCard key={vendor.id} vendor={vendor} />
          ))}
        </div>
      </div>

      {/* Pagination Dots */}
      {canNavigate && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentPage
                  ? 'w-8 bg-primary'
                  : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to page ${index + 1}`}
              aria-current={index === currentPage ? 'true' : 'false'}
            />
          ))}
        </div>
      )}
    </div>
  );
}
